import {
  ButtonIcon,
  FieldGroup,
  ModalProps,
  ModalRush,
  ModalRushPage,
  SelectCountry,
  ToastSeverity,
  useToast,
} from '@/ui'
import { Button, Checkbox, FormErrorMessage, Input, Select, cn, formatHookFormErrors } from '@dashboard/ui'
import { Controller, useForm } from 'react-hook-form'
import { BenefitSourceType, Country, Currency, Locale, ReceiptParams } from '@/api/dashboard'

import { dashboardClient } from '@/api'
import { SUPER_ADMIN_PATH, useGenerateCompanyPath, useNavigateBack } from '@/libs'
import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { XClose } from '@/icons'
import { SelectSkuTable } from '@/layouts/components/SelectSkuTable'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'

export const ReceiptTestForm = (props: ModalProps) => {
  const { companyId, gameId } = useGenerateCompanyPath()
  const [html, setHtml] = useState<string>('')
  const { user } = useAuth0()
  const showToast = useToast()
  const [width, setWidth] = useState('590px')
  const { t } = useTranslation()

  const [benefits, setBenefits] = useState<string>(
    JSON.stringify(
      [
        {
          type: 'bonus_item',
          source: {
            type: BenefitSourceType.BonusItem,
          },
          data: {
            kind: 'bonus_item',
            item: {
              sku: 'sku',
              id: '123123123',
              name: 'Bonus Item',
              description: 'This is a bonus item',
              image_url: 'https://static.aghanim.com/images/stores/amazon-appstore.svg',
              type: 'item',
              quantity: 8812,
            },
          },
        },
      ],
      null,
      2,
    ),
  )

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<ReceiptParams>({
    defaultValues: {
      item_ids: [],
      order_payment: {
        id: '123123123',
        payment_method_caption: 'cards',
        payment_number: '123123123123123',
        payment_method_name: 'Cards',
        payment_method_logo_url: 'https://static.aghanim.com/payment-methods/png/mastercard.png',
        card_last_4_digits: '4242',
        card_scheme: 'mastercard',
        subtotal: 1000,
        total: 1000,
        tax: {
          amount: 20,
          name: 'sales_tax',
          included: true,
          visible: true,
        },
        email: user?.email || 'test@aghanim.com',
        currency: Currency.USD,
        created_at: Math.round(new Date().getTime() / 1000),
        billing_address: {
          address1: '123 Test St',
          city: 'Test City',
          postal: '12345',
        },
      },
      currency: Currency.USD,
      locale: Locale.En,
      rewards_visible: false,
      reward_points: '10',
      loyalty_points: '15',
      appreciation_text: 'Appreciation Text',
      sandbox: false,
      benefits: [],
      country: Country.US,
      promocode: {
        code: '',
        discount_amount_usd: 0,
      },
      user_name: 'Test User',
    },
  })

  useEffect(() => {
    const loadItem = async () => {
      const { data } = await dashboardClient.v1.getItems({
        companyId,
        gameId,
        limit: 5,
      })

      if (data.length) {
        setValue('item_ids', [data[0].id])

        const payload = getValues()
        payload.item_ids = [data[0].id]

        updateEmail(payload)
      }
    }

    loadItem()
  }, [])

  const updateEmail = async (payload: ReceiptParams) => {
    if (!payload.promocode?.code) {
      delete payload.promocode
    }

    payload.benefits = benefits ? JSON.parse(benefits) : []

    const { data } = await dashboardClient.v1.makeReceiptEmailFromParameters(companyId, gameId, payload)
    setHtml(data.html)
  }

  const onSendEmail = async () => {
    const payload = getValues()
    if (!payload.promocode?.code) {
      delete payload.promocode
    }

    await dashboardClient.v1.sendReceiptEmailFromParameters(companyId, gameId, payload)
    showToast({ message: 'Email has been sent', severity: ToastSeverity.success })
  }

  return (
    <ModalRush {...props}>
      <div className="relative flex size-full gap-10">
        <div className="absolute right-1 top-1 flex w-full justify-end">
          <ButtonIcon size="sm" variant="outline" color="secondary" onClick={props.onClose}>
            <XClose />
          </ButtonIcon>
        </div>
        <div className="flex h-full w-1/2 gap-3 overflow-auto">
          <div className="w-1/2">
            <FieldGroup label={'email'}>
              <Input {...register('order_payment.email')} {...formatHookFormErrors(errors, 'email')} />
            </FieldGroup>

            <FieldGroup label={'payment_number'}>
              <Input
                {...register('order_payment.payment_number')}
                {...formatHookFormErrors(errors, 'payment_number')}
              />
            </FieldGroup>

            <FieldGroup label={'payment_method'}>
              <Input
                {...register('order_payment.payment_method_name')}
                {...formatHookFormErrors(errors, 'order_payment.payment_method_name')}
              />
            </FieldGroup>

            <FieldGroup label={'payment_logo_url'}>
              <Input
                {...register('order_payment.payment_method_logo_url')}
                {...formatHookFormErrors(errors, 'order_payment.payment_method_logo_url')}
              />
            </FieldGroup>

            <FieldGroup label={t('players.country')}>
              <Controller control={control} name="country" render={({ field }) => <SelectCountry {...field} />} />
            </FieldGroup>

            <FieldGroup label={'Width'}>
              <Select
                value={width}
                onChange={v => setWidth(v as string)}
                items={[
                  {
                    value: '640px',
                    children: '640px',
                  },
                  {
                    value: '590px',
                    children: '590px',
                  },
                ]}
              />
            </FieldGroup>
            <Controller
              control={control}
              name="sandbox"
              render={({ field }) => (
                <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)}>
                  sandbox
                </Checkbox>
              )}
            />
            <div className="mt-2">
              <Controller
                name="item_ids"
                control={control}
                rules={{ required: t('validation.required') }}
                render={({ field }) => (
                  <SelectSkuTable
                    onChange={v => {
                      field.onChange(v)
                    }}
                    value={field.value || []}
                  />
                )}
              />
              <ErrorMessage
                name="item_ids"
                errors={errors}
                render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
              />
            </div>

            <FieldGroup label={'Benefits'} className="mt-2">
              <textarea
                className="w-full rounded-lg border border-border-primary p-2"
                value={benefits}
                onChange={e => setBenefits(e.target.value)}
              />
            </FieldGroup>
          </div>
          <div className="w-1/2">
            <FieldGroup label={'subtotal'}>
              <Input
                {...register('order_payment.subtotal')}
                type={'number'}
                {...formatHookFormErrors(errors, 'subtotal')}
              />
            </FieldGroup>

            <FieldGroup label={'total'}>
              <Input {...register('order_payment.total')} type={'number'} {...formatHookFormErrors(errors, 'total')} />
            </FieldGroup>

            <FieldGroup label={'currency'}>
              <Controller
                control={control}
                name="currency"
                render={({ field }) => (
                  <Select
                    {...field}
                    items={Object.values(Currency)
                      .sort()
                      .map(value => ({
                        value: value,
                        children: value,
                      }))}
                  />
                )}
              />
            </FieldGroup>

            <div className="mb-[20px] h-[58px] pt-7">
              <Controller
                control={control}
                name="rewards_visible"
                render={({ field }) => (
                  <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)}>
                    rewards_visible
                  </Checkbox>
                )}
              />
            </div>

            <FieldGroup label={'reward_points'}>
              <Input
                {...register('reward_points')}
                type={'number'}
                {...formatHookFormErrors(errors, 'reward_points')}
              />
            </FieldGroup>

            <FieldGroup label={'loyalty_points'}>
              <Input
                {...register('loyalty_points')}
                type={'number'}
                {...formatHookFormErrors(errors, 'loyalty_points')}
              />
            </FieldGroup>

            <div className="mb-[20px] h-[58px] pt-7">
              <Controller
                control={control}
                name="order_payment.tax.visible"
                render={({ field }) => (
                  <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)}>
                    tax_visible
                  </Checkbox>
                )}
              />
            </div>
            <FieldGroup label={'tax_name'}>
              <Input
                {...register('order_payment.tax.name')}
                {...formatHookFormErrors(errors, 'order_payment.tax.name')}
              />
            </FieldGroup>

            <FieldGroup label={'tax_amount'}>
              <Input
                {...register('order_payment.tax.amount')}
                type={'number'}
                {...formatHookFormErrors(errors, 'tax_amount')}
              />
            </FieldGroup>

            <FieldGroup label={'appreciation_text'}>
              <Input {...register('appreciation_text')} {...formatHookFormErrors(errors, 'appreciation_text')} />
            </FieldGroup>

            <FieldGroup label={'promocode.code'}>
              <Input {...register('promocode.code')} {...formatHookFormErrors(errors, 'promocode.code')} />
            </FieldGroup>

            <FieldGroup label={'promocode.discount_amount_usd'}>
              <Input
                {...register('promocode.discount_amount_usd')}
                type={'number'}
                {...formatHookFormErrors(errors, 'promocode.discount_amount_usd')}
              />
            </FieldGroup>
          </div>

          <div className="flex flex-col gap-1">
            <Button type="button" onClick={handleSubmit(updateEmail)}>
              Update
            </Button>

            <Button type="button" onClick={onSendEmail} variant="outline">
              Send
            </Button>
          </div>
        </div>
        <div
          className={cn(
            'ph-no-capture flex h-full w-1/2 flex-col items-center justify-center rounded-xl border-2 border-border-secondary bg-fg-secondary-hover',
          )}
        >
          <iframe
            src={'data:text/html,' + encodeURIComponent(html)}
            frameBorder={0}
            style={{
              height: '1000px',
              width: width,
            }}
          />
        </div>
      </div>
    </ModalRush>
  )
}

export const ReceiptTestPage = () => {
  const back = useNavigateBack({ fallback: SUPER_ADMIN_PATH })

  return <ModalRushPage back={back}>{props => <ReceiptTestForm {...props} />}</ModalRushPage>
}
